import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useUserStore } from '@/stores/userStore';
import { api } from '@/lib/api';
import { completeSaIdLogin, startSaIdLogin } from '@/lib/saidClient';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  loginWithSaId: (returnTo?: string) => Promise<void>;
  linkOAuthProvider: (provider: 'zoho' | 'github', returnTo?: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const setUser = useUserStore(s => s.setUser);
  const clearUser = useUserStore(s => s.clearUser);

  useEffect(() => {
    checkSession();
  }, []);

  const checkSession = async () => {
    try {
      try {
        if (window.location.pathname.startsWith('/auth/said/callback')) {
          const saidCallback = await completeSaIdLogin();
          if (saidCallback?.token) {
            const response = await api.auth.saidExchange({ token: saidCallback.token });
            if (!response.success || !response.data?.token || !response.data?.user) {
              throw new Error(response.error?.message || 'Could not complete SaID sign in');
            }
            localStorage.setItem('flexiplanner-session-token', response.data.token);
            applyUser(response.data.user);
            setIsAuthenticated(true);
            window.location.replace(saidCallback.returnTo || '/teams');
            return;
          }
        }

        const providerCallback = consumeProviderOAuthCallback();
        if (providerCallback?.token) {
          localStorage.setItem('flexiplanner-session-token', providerCallback.token);
          const response = await api.auth.session();
          if (!response.success || !response.data?.user) {
            throw new Error(response.error?.message || 'Could not complete provider sign in');
          }
          applyUser(response.data.user);
          setIsAuthenticated(true);
          window.location.replace(providerCallback.returnTo || '/teams');
          return;
        }
        if (providerCallback?.error) {
          throw new Error(providerCallback.error);
        }

      } catch (error) {
        console.error('Auth callback failed:', error);
        localStorage.removeItem('flexiplanner-session-token');
        clearUser();
        setIsAuthenticated(false);
        return;
      }

      const token = localStorage.getItem('flexiplanner-session-token');
      if (!token) {
        setIsAuthenticated(false);
        return;
      }

      const response = await api.auth.session();
      if (response.success && response.data?.user) {
        applyUser(response.data.user);
        setIsAuthenticated(true);
      } else {
        localStorage.removeItem('flexiplanner-session-token');
        clearUser();
        setIsAuthenticated(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithSaId = async (returnTo = '/teams') => {
    await startSaIdLogin(returnTo);
  };

  const linkOAuthProvider = async (provider: 'zoho' | 'github', returnTo = window.location.pathname || '/settings') => {
    const response = provider === 'zoho'
      ? await api.auth.zohoLinkAuthUrl(returnTo)
      : await api.auth.githubLinkAuthUrl(returnTo);
    if (!response.success || !response.data?.url) {
      throw new Error(response.error?.message || `${provider} account linking is not available.`);
    }
    window.location.href = response.data.url;
  };

  const logout = async () => {
    try {
      await api.auth.logout();
    } finally {
      localStorage.removeItem('flexiplanner-session-token');
      localStorage.removeItem('sahast-session-token');
      clearUser();
      setIsAuthenticated(false);
    }
  };

  const applyUser = (user: any) => {
    setUser({
      id: user.id || user.user_id,
      email: user.email,
      displayName: user.display_name || user.displayName,
      timezone: user.timezone,
      onboardingCompleted: Boolean(user.onboarding_completed),
      settings: serverSettingsToClient(user.settings),
    });
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, loginWithSaId, linkOAuthProvider, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

function serverSettingsToClient(settings: any) {
  if (!settings) return undefined;
  return {
    theme: settings.theme,
    defaultView: settings.default_view,
    workHours: settings.work_hours,
    workDays: settings.work_days,
  };
}

function consumeProviderOAuthCallback(): { token?: string; error?: string; returnTo?: string } | null {
  if (typeof window === 'undefined') return null;
  const path = window.location.pathname;
  if (!path.startsWith('/auth/zoho/callback') && !path.startsWith('/auth/github/callback')) return null;
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const token = params.get('oauth_token') || undefined;
  const error = params.get('oauth_error') || undefined;
  const returnTo = params.get('return_to') || '/teams';
  window.history.replaceState({}, document.title, path);
  return { token, error, returnTo: returnTo.startsWith('/') ? returnTo : '/teams' };
}
